import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogRef } from '@angular/cdk/dialog';
import {
  isIosDevice,
  resolveStoreUrl,
} from '../../../core/platform/store-url.util';
import { environment } from '../../../environments/environment';

/**
 * Prompt BUILT-IN de descarga de la app Art Stgo (frente #2 / captación).
 *
 * A diferencia del SeasonalPopupComponent (que renderiza un popup configurado
 * por el admin), este componente trae su contenido embebido (logo + copy + CTA)
 * y NO depende del backend ni de `popupService.getActive()`. Lo gatilla
 * AppComponent en la llegada por QR (`?src=qr`) para garantizar que un visitante
 * que escanea el QR de un artista y cae en la web —sin la app— SIEMPRE vea la
 * invitación a descargarla, independiente de si hay un popup admin activo.
 */
@Component({
  selector: 'app-download-app-prompt',
  templateUrl: './download-app-prompt.component.html',
  styleUrls: ['./download-app-prompt.component.css'],
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DownloadAppPromptComponent implements OnDestroy {
  private _el = inject(ElementRef<HTMLElement>);
  private _dialogRef = inject(DialogRef<DownloadAppPromptComponent>);
  private _exitTimer: ReturnType<typeof setTimeout> | null = null;

  /** Logo blanco sobre el fondo oscuro de la tarjeta. */
  readonly logoUrl = '/images/logo/art-stgo-logo-white.png';
  readonly isDesktop =
    !isIosDevice(navigator.userAgent) && !/android/i.test(navigator.userAgent);

  ngOnDestroy(): void {
    if (this._exitTimer !== null) {
      clearTimeout(this._exitTimer);
    }
  }

  /**
   * En móvil abre su tienda correspondiente; en desktop cada CTA explicita la
   * tienda elegida. Ambos destinos usan una pestaña aislada.
   */
  download(store?: 'app-store' | 'play-store'): void {
    const stores = {
      appStoreUrl: environment.appStoreUrl,
      playStoreUrl: environment.playStoreUrl,
    };
    const url =
      store === 'app-store'
        ? stores.appStoreUrl
        : store === 'play-store'
          ? stores.playStoreUrl
          : resolveStoreUrl(navigator.userAgent, stores);
    window.open(url, '_blank', 'noopener,noreferrer');
    this._closeWithAnimation();
  }

  close(): void {
    this._closeWithAnimation();
  }

  private _closeWithAnimation(): void {
    const container = this._el.nativeElement.querySelector(
      '.dl-container',
    ) as HTMLElement | null;
    if (container) {
      container.style.animation = 'dl-exit 200ms ease-in forwards';
    }
    this._exitTimer = setTimeout(() => {
      this._exitTimer = null;
      this._dialogRef.close();
    }, 190);
  }
}
